<p-toast></p-toast>

<div class="management-shell">
  @if (error()) {
    <div class="error-state">{{ error() }}</div>
  } @else if (company(); as c) {
    <section class="page-hero">
      <div class="hero-main">
        <div class="entity-logo">
          @if (c.logo) {
            <img [src]="c.logo" [alt]="c.name" />
          } @else {
            <i class="pi pi-building"></i>
          }
        </div>
        <div>
          <span class="eyebrow">Company</span>
          <h1 class="hero-title">{{ c.name }}</h1>
          <div class="hero-meta">
            <span class="chip">CR {{ c.crNumber || 'N/A' }}</span>
            <span class="chip">VAT {{ c.taxRegNumber || 'N/A' }}</span>
            @if (c.enableEnterpriseFeatures) {
              <span class="chip">Enterprise</span>
            }
          </div>
        </div>
      </div>

      <div class="hero-actions">
        <button pButton type="button" label="Invite Employee" icon="pi pi-user-plus" (click)="openInviteDialog()"></button>
        <button pButton type="button" label="Edit Company" icon="pi pi-pencil" severity="secondary" [routerLink]="['/company/edit']"></button>
      </div>
    </section>

    <div class="content-grid">
      <section class="panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">Company Information</span>
            <h2>Business profile</h2>
          </div>
        </div>

        <div class="details-list">
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-map-marker"></i> Address</span>
            <span class="detail-value">{{ c.address || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-phone"></i> Phone</span>
            <span class="detail-value">{{ c.phoneNumber || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-envelope"></i> Email</span>
            <span class="detail-value">{{ c.emailAddress || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-globe"></i> Website</span>
            <span class="detail-value">{{ c.websiteURL || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-inbox"></i> PO Box</span>
            <span class="detail-value">{{ c.poBox || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-flag"></i> Country ID</span>
            <span class="detail-value">{{ c.countryId || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-truck"></i> Transportation Agency</span>
            <span class="detail-value">{{ c.transportationAgencyId || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-send"></i> Freight Forwarder</span>
            <span class="detail-value">{{ c.freightForwarderId || 'N/A' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-sitemap"></i> Enterprise Features</span>
            <span class="detail-value">{{ formatBoolean(c.enableEnterpriseFeatures) }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label"><i class="pi pi-user"></i> Default Contact</span>
            <span class="detail-value">{{ c.defaultContactId || 'N/A' }}</span>
          </div>
        </div>
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">Transportation Agency</span>
            <h2>{{ transportationAgency() ? 'Registered carrier profile' : 'Not registered' }}</h2>
          </div>
          @if (!transportationAgency()) {
            <button
              pButton
              type="button"
              label="Register as Agency"
              icon="pi pi-truck"
              [loading]="isRegisteringAgency()"
              (click)="openTransportationAgencyDialog()">
            </button>
          }
        </div>

        @if (transportationAgency(); as agency) {
          <div class="details-list">
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-building"></i> Agency Name</span>
              <span class="detail-value">{{ agency.agencyName || c.name || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-hashtag"></i> Agency ID</span>
              <span class="detail-value">{{ agency.rowId || c.transportationAgencyId || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-id-card"></i> MOT License</span>
              <span class="detail-value">{{ agency.motAgencyLicenseNumber || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-briefcase"></i> CR Number</span>
              <span class="detail-value">{{ agency.crNumber || c.crNumber || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-phone"></i> Phone</span>
              <span class="detail-value">{{ agency.phoneNumber || c.phoneNumber || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-envelope"></i> Email</span>
              <span class="detail-value">{{ agency.emailAddress || c.emailAddress || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-globe"></i> Website</span>
              <span class="detail-value">{{ agency.website || c.websiteURL || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-user"></i> Default Contact</span>
              <span class="detail-value">{{ agency.defaultContactId || c.defaultContactId || 'N/A' }}</span>
            </div>
          </div>
        } @else {
          <div class="empty-state">
            This company is not registered as a transportation agency. Register it to unlock carrier fleet and availability workflows.
          </div>
        }
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">Freight Forwarder</span>
            <h2>{{ freightForwarder() ? 'Registered forwarder profile' : 'Not registered' }}</h2>
          </div>
          @if (!freightForwarder()) {
            <button
              pButton
              type="button"
              label="Register as Forwarder"
              icon="pi pi-send"
              [loading]="isRegisteringForwarder()"
              (click)="registerFreightForwarder()">
            </button>
          }
        </div>

        @if (freightForwarder(); as forwarder) {
          <div class="details-list">
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-building"></i> Forwarder Name</span>
              <span class="detail-value">{{ forwarder.name || c.name || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-hashtag"></i> Forwarder ID</span>
              <span class="detail-value">{{ forwarder.id || c.freightForwarderId || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-briefcase"></i> CR Number</span>
              <span class="detail-value">{{ forwarder.crNumber || c.crNumber || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-phone"></i> Phone</span>
              <span class="detail-value">{{ forwarder.phoneNumber || c.phoneNumber || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-envelope"></i> Email</span>
              <span class="detail-value">{{ forwarder.emailAddress || c.emailAddress || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-globe"></i> Website</span>
              <span class="detail-value">{{ forwarder.webSiteUrl || c.websiteURL || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-map-marker"></i> Address</span>
              <span class="detail-value">{{ forwarder.address || c.address || 'N/A' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label"><i class="pi pi-user"></i> Default Contact</span>
              <span class="detail-value">{{ forwarder.defaultContactId || c.defaultContactId || 'N/A' }}</span>
            </div>
          </div>
        } @else {
          <div class="empty-state">
            This company is not registered as a freight forwarder. Register it to enable forwarding workflows.
          </div>
        }
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">Employees</span>
            <h2>{{ employees().length }} customers</h2>
          </div>
        </div>

        @if (employees().length) {
          <table class="employee-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Role</th>
                <th>Access Roles</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (employee of employees(); track employee.id) {
                <tr>
                  <td>
                    <div class="person-cell">
                      <p-avatar [image]="employee.imageUrl || undefined" [label]="employee.imageUrl ? undefined : (employee.name || '?').charAt(0)" shape="circle"></p-avatar>
                      <div>
                        <span class="person-name">{{ employee.name || 'Unnamed' }}</span>
                        <span class="person-subtext">{{ employee.emailAddress || employee.phoneNumber || 'No contact' }}</span>
                      </div>
                    </div>
                  </td>
                  <td><i class="pi pi-id-card mr-2 text-primary"></i>{{ employee.corporateRole || 'N/A' }}</td>
                  <td>
                    <div class="chip-row">
                      @for (roleName of roleNames(employee); track roleName) {
                        <span class="chip"><i class="pi pi-shield"></i>{{ roleName }}</span>
                      } @empty {
                        <span class="person-subtext">No roles</span>
                      }
                    </div>
                  </td>
                  <td>
                    <div class="row-actions">
                      <button pButton type="button" icon="pi pi-eye" [rounded]="true" [text]="true" [routerLink]="['/company/employees', employee.id]" pTooltip="View"></button>
                      <button pButton type="button" icon="pi pi-pencil" [rounded]="true" [text]="true" [routerLink]="['/company/employees', employee.id, 'edit']" pTooltip="Edit"></button>
                      <button pButton type="button" icon="pi pi-shield" [rounded]="true" [text]="true" (click)="openRoleDialog(employee)" pTooltip="Manage roles"></button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <div class="empty-state">No employees are linked to this company yet.</div>
        }
      </section>

      <section class="panel">
        <div class="panel-header">
          <div>
            <span class="eyebrow">Files</span>
            <h2>{{ companyFiles().length }} company documents</h2>
          </div>
          <button pButton type="button" label="Add File" icon="pi pi-plus" (click)="openFileDialog()"></button>
        </div>

        @if (companyFiles().length) {
          <table class="employee-table">
            <thead>
              <tr>
                <th>File Type</th>
                <th>File Name</th>
                <th>Uploaded By</th>
                <th>Uploaded At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (file of companyFiles(); track file.id) {
                <tr>
                  <td><span class="chip"><i class="pi pi-file"></i>{{ fileTypeLabel(file.category) }}</span></td>
                  <td>{{ file.fileName }}</td>
                  <td>{{ file.uploadedByCustomerName || 'N/A' }}</td>
                  <td>{{ file.uploadedAt | date:'medium' }}</td>
                  <td>
                    <div class="row-actions">
                      <a pButton type="button" icon="pi pi-external-link" [rounded]="true" [text]="true"
                        [href]="file.fileUrl" target="_blank" rel="noopener" pTooltip="Open"></a>
                      <button pButton type="button" icon="pi pi-trash" [rounded]="true" [text]="true"
                        severity="danger" (click)="deleteCompanyFile(file)" pTooltip="Delete"></button>
                    </div>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        } @else {
          <div class="empty-state">No company documents have been uploaded.</div>
        }
      </section>
    </div>
  } @else {
    <div class="empty-state">Loading company...</div>
  }
</div>

<p-dialog header="Invite Employee" [visible]="inviteDialogVisible()" (visibleChange)="inviteDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(92vw, 720px)' }">
  <form [formGroup]="inviteForm" class="form-grid" (ngSubmit)="inviteEmployee()">
    <div class="field">
      <label for="inviteName">Name</label>
      <input pInputText id="inviteName" formControlName="name" />
    </div>
    <div class="field">
      <label for="inviteEmail">Email</label>
      <input pInputText id="inviteEmail" formControlName="emailAddress" />
    </div>
    <div class="field">
      <label for="invitePhone">Phone</label>
      <input pInputText id="invitePhone" formControlName="phoneNumber" />
    </div>
    <div class="field">
      <label for="inviteCorporateRole">Corporate Role</label>
      <input pInputText id="inviteCorporateRole" formControlName="corporateRole" />
    </div>
    <div class="field full">
      <label for="inviteAddress">Address</label>
      <textarea pInputText id="inviteAddress" formControlName="address"></textarea>
    </div>
    <div class="field full">
      <label>Access Roles</label>
      <div class="role-grid">
        @for (role of roles(); track role.roleId) {
          <label class="role-option">
            <input type="checkbox" [checked]="inviteRoleIds().has(role.roleId)" (change)="toggleInviteRole(role.roleId, $any($event.target).checked)" />
            <span>{{ role.roleName }}</span>
          </label>
        } @empty {
          <span class="person-subtext"><i class="pi pi-info-circle mr-2"></i>No roles available.</span>
        }
      </div>
    </div>
    <div class="form-actions full">
      <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="inviteDialogVisible.set(false)"></button>
      <button pButton type="submit" label="Send Invite" icon="pi pi-send" [loading]="isSaving()"></button>
    </div>
  </form>
</p-dialog>

<p-dialog header="Register Transportation Agency" [visible]="transportationAgencyDialogVisible()" (visibleChange)="transportationAgencyDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(92vw, 560px)' }">
  <form [formGroup]="transportationAgencyForm" class="form-grid" (ngSubmit)="registerTransportationAgency()">
    <div class="field full">
      <label for="motAgencyLicenseNumber">MOT Agency License Number</label>
      <input pInputText id="motAgencyLicenseNumber" formControlName="motAgencyLicenseNumber" />
    </div>
    <div class="form-actions full">
      <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="transportationAgencyDialogVisible.set(false)"></button>
      <button pButton type="submit" label="Register Agency" icon="pi pi-truck" [loading]="isRegisteringAgency()"></button>
    </div>
  </form>
</p-dialog>

<p-dialog header="Add Company File" [visible]="fileDialogVisible()" (visibleChange)="fileDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(92vw, 560px)' }">
  <div class="form-grid">
    <div class="field full">
      <label for="companyFileType">File Type</label>
      <select id="companyFileType" [value]="selectedFileType()" (change)="onFileTypeChanged($any($event.target).value)">
        @for (type of companyFileTypes; track type.value) {
          <option [value]="type.value">{{ type.label }}</option>
        }
      </select>
    </div>
    <div class="field full">
      <label for="companyFile">File</label>
      <input id="companyFile" type="file" (change)="onCompanyFileSelected($event)" />
    </div>
    <div class="form-actions full">
      <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="fileDialogVisible.set(false)"></button>
      <button pButton type="button" label="Upload" icon="pi pi-upload" [loading]="isUploadingFile()" [disabled]="!selectedFile() || isUploadingFile()" (click)="uploadCompanyFile()"></button>
    </div>
  </div>
</p-dialog>

<p-dialog header="Manage Employee Roles" [visible]="roleDialogVisible()" (visibleChange)="roleDialogVisible.set($event)" [modal]="true" [style]="{ width: 'min(92vw, 640px)' }">
  @if (roleCustomer(); as employee) {
    <div class="panel-header">
      <div>
        <span class="eyebrow">{{ employee.name }}</span>
        <h2>Access roles</h2>
      </div>
    </div>
    <div class="role-grid">
      @for (role of roles(); track role.roleId) {
        <label class="role-option">
          <input type="checkbox" [checked]="selectedRoleIds().has(role.roleId)" (change)="toggleSelectedRole(role.roleId, $any($event.target).checked)" />
          <span><i class="pi pi-shield mr-2"></i>{{ role.roleName }}</span>
        </label>
      }
    </div>
    <div class="form-actions mt-3">
      <button pButton type="button" label="Cancel" severity="secondary" [text]="true" (click)="roleDialogVisible.set(false)"></button>
      <button pButton type="button" label="Save Roles" icon="pi pi-save" [loading]="isSaving()" (click)="saveRoles()"></button>
    </div>
  }
</p-dialog>
